import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchControllers } from '@/features/controllers/api'
import { fetchAlarmTopics } from '@/features/alarmTopics/api'
import { applyFleetBulkOverride, applyFleetSeverityDefault, fetchFleetOverview } from '@/features/fleetAlarms/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/** Ports includes/fleet_alarm_subscriptions_manage.html — per-severity
 * immediate-save default toggles, plus a bulk subscribe/unsubscribe tool
 * across a chosen set of systems and topics. topics_by_severity is grouped
 * client-side from the flat GET /portal/settings/alarm-topics response,
 * same grouping the legacy server-side view does. */
export default function FleetAlarmSubscriptionsManagePage() {
  const queryClient = useQueryClient()
  const overviewQuery = useQuery({ queryKey: ['fleet-alarm-overview'], queryFn: fetchFleetOverview })
  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })
  const topicsQuery = useQuery({ queryKey: ['alarm-topics'], queryFn: fetchAlarmTopics })

  const [selectedControllers, setSelectedControllers] = useState<Set<string>>(new Set())
  const [selectedTopics, setSelectedTopics] = useState<Set<number>>(new Set())
  const [bulkMessage, setBulkMessage] = useState<string | null>(null)

  const topicsBySeverity = useMemo(() => {
    const map = new Map<string, typeof topicsQuery.data extends undefined ? never : NonNullable<typeof topicsQuery.data>['alarm_topics']>()
    for (const t of topicsQuery.data?.alarm_topics ?? []) {
      const key = t.alarm_severity_name || 'Uncategorized'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(t)
    }
    return map
  }, [topicsQuery.data])

  const severityMutation = useMutation({
    mutationFn: ({ id, isSubscribed }: { id: number; isSubscribed: boolean }) =>
      applyFleetSeverityDefault(id, isSubscribed),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fleet-alarm-overview'] }),
  })

  const bulkMutation = useMutation({
    mutationFn: (isSubscribed: boolean) => {
      const overrides: Record<number, boolean> = {}
      for (const topicId of selectedTopics) overrides[topicId] = isSubscribed
      return applyFleetBulkOverride(Array.from(selectedControllers), overrides)
    },
    onSuccess: (result) => {
      setBulkMessage(
        `Applied to ${result.topics_updated} topic(s) across ${result.systems_updated} system(s).` +
          (result.skipped_controller_ids.length
            ? ` ${result.skipped_controller_ids.length} system(s) were skipped (not accessible to you).`
            : ''),
      )
      queryClient.invalidateQueries({ queryKey: ['fleet-alarm-overview'] })
    },
  })

  function toggleController(id: string) {
    setSelectedControllers((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleTopic(id: number) {
    setSelectedTopics((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (overviewQuery.isLoading) return <LoadingState label="Loading…" />
  if (overviewQuery.isError) {
    return (
      <ErrorState
        message={overviewQuery.error instanceof Error ? overviewQuery.error.message : 'Failed to load.'}
        onRetry={() => overviewQuery.refetch()}
      />
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-text-primary">Manage Fleet Subscriptions</h1>

      <section className="rounded border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">Severity Defaults</h2>
        <div className="space-y-2">
          {(overviewQuery.data?.severities ?? []).map((sev) => (
            <div key={sev.alarm_severity_id} className="flex items-center justify-between text-sm">
              <span className="text-text-primary">{sev.alarm_severity_name}</span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sev.is_default_subscribed}
                  disabled={severityMutation.isPending}
                  onChange={(e) => severityMutation.mutate({ id: sev.alarm_severity_id, isSubscribed: e.target.checked })}
                />
                <span className="text-xs text-text-secondary">
                  {sev.systems_affected}/{sev.total_systems} subscribed
                </span>
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">Bulk Apply</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="mb-1 text-xs text-text-secondary">
              Systems ({selectedControllers.size} selected)
            </p>
            <div className="h-48 overflow-y-auto rounded border border-border p-2">
              {(controllersQuery.data ?? []).map((c) => (
                <label key={c.controller_id} className="flex items-center gap-2 py-0.5 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={selectedControllers.has(c.controller_id)}
                    onChange={() => toggleController(c.controller_id)}
                  />
                  {c.controller_name || c.controller_id}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs text-text-secondary">Topics ({selectedTopics.size} selected)</p>
            <div className="h-48 overflow-y-auto rounded border border-border p-2">
              {Array.from(topicsBySeverity.entries()).map(([severityName, topics]) => (
                <div key={severityName} className="mb-2">
                  <p className="text-xs font-semibold text-text-secondary">{severityName}</p>
                  {topics.map((t) => (
                    <label key={t.alarm_topic_id} className="flex items-center gap-2 py-0.5 text-sm text-text-primary">
                      <input
                        type="checkbox"
                        checked={selectedTopics.has(t.alarm_topic_id)}
                        onChange={() => toggleTopic(t.alarm_topic_id)}
                      />
                      {t.alarm_real_name}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {bulkMessage && <p className="mt-3 text-sm text-text-primary">{bulkMessage}</p>}

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={selectedControllers.size === 0 || selectedTopics.size === 0 || bulkMutation.isPending}
            onClick={() => bulkMutation.mutate(true)}
            className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Apply: Subscribe
          </button>
          <button
            type="button"
            disabled={selectedControllers.size === 0 || selectedTopics.size === 0 || bulkMutation.isPending}
            onClick={() => bulkMutation.mutate(false)}
            className="rounded border border-border px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
          >
            Apply: Unsubscribe
          </button>
        </div>
      </section>
    </div>
  )
}
